import { Employee } from '../../types';
import { supabase } from '../lib/supabase';

const normalizeId = (value: unknown): string => String(value || '').trim();

const findEmployeeIdByColumn = async (
  column: 'id' | 'auth_user_id',
  value: string,
  companyId: string
): Promise<string | null> => {
  if (!value || !companyId) return null;

  const { data, error } = await supabase
    .from('employees')
    .select('id')
    .eq(column, value)
    .eq('company_id', companyId)
    .maybeSingle();

  if (error) {
    console.warn(`Failed to resolve approval employee id by ${column}:`, error);
    return null;
  }

  return normalizeId((data as any)?.id) || null;
};

export const resolveEmployeeIdForApproval = async (
  candidateId: unknown,
  companyId: unknown
): Promise<string | null> => {
  const normalizedCandidateId = normalizeId(candidateId);
  const normalizedCompanyId = normalizeId(companyId);
  if (!normalizedCandidateId) return null;

  const employeeId =
    (await findEmployeeIdByColumn('id', normalizedCandidateId, normalizedCompanyId)) ||
    (await findEmployeeIdByColumn('auth_user_id', normalizedCandidateId, normalizedCompanyId));

  return employeeId || normalizedCandidateId;
};

export const resolveCurrentEmployeeIdForApproval = async (
  currentUser: Employee,
  companyId: unknown
): Promise<string | null> => {
  const normalizedCompanyId = normalizeId(companyId);
  let authUserId = normalizeId(currentUser.auth_user_id);

  try {
    const getUser = (supabase.auth as any)?.getUser;
    if (typeof getUser === 'function') {
      const { data, error } = await getUser.call(supabase.auth);
      if (!error) {
        authUserId = normalizeId(data?.user?.id) || authUserId;
      }
    }
  } catch (error) {
    console.warn('Failed to resolve authenticated user for approval request:', error);
  }

  if (authUserId && normalizedCompanyId) {
    const employeeId = await findEmployeeIdByColumn('auth_user_id', authUserId, normalizedCompanyId);
    if (employeeId) return employeeId;
  }

  return resolveEmployeeIdForApproval(currentUser.id, normalizedCompanyId);
};
